import { useState, type ReactNode } from 'react';
import { Banner, Button, Dialog, Toaster, type Toast } from '@forecastify/ui';
import {
  demoDataRemovedSchema,
  demoDataSummarySchema,
  type DemoDataSummary,
} from '@forecastify/shared';
import { useT } from '../../i18n';
import { useSession } from '../../session';
import { usePermissionSet } from '../permissions-hook';

let toastCounter = 0;
const nextToastId = (): string => `demo-${String((toastCounter += 1))}`;

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ F-5 · "this workspace holds demo data", and the way to remove it.          │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Drawn above the shell while the sidebar read says there is demo data, for everybody. The link is
 * drawn only for `settings.manage` — and the server refuses everybody else regardless.
 *
 * ⚠️⚠️ **THE CONFIRMATION ASKS THE SERVER WHAT WOULD HAPPEN BEFORE IT ASKS THE PERSON.** "12 items
 * stay" is the reassurance that makes a final, unrecoverable removal something people dare to press,
 * and only `demo_purge` can work it out — the fixed point over every foreign key is not something a
 * client could approximate. The counts are rows, so the copy says *items*, never *your changes*.
 *
 * ⚠️ **Nothing is refetched by hand after the removal.** `workspace.demo_purge` reaches every open
 * screen as a resync (`WORKSPACE_WIDE_ACTIONS`), the sidebar read included — which is also what
 * takes this banner away.
 */
export function DemoBanner(): ReactNode {
  const t = useT();
  const session = useSession();
  const permissions = usePermissionSet();
  const canRemove = permissions.has('settings.manage');

  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState<DemoDataSummary | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toasts, setToasts] = useState<readonly Toast[]>([]);

  const errorFor = async (response: Response | null): Promise<string> => {
    if (response === null) return t('error.unavailable');
    const body = (await response.json().catch(() => null)) as {
      error?: { code?: string };
    } | null;
    return body?.error?.code === 'demo_purge_incomplete'
      ? t('demo.error.incomplete')
      : t('error.unavailable');
  };

  const openDialog = (): void => {
    setOpen(true);
    setSummary(null);
    setError(null);
    void (async () => {
      const response = await session.request('/workspace/demo').catch(() => null);
      if (response?.ok !== true) {
        setError(await errorFor(response));
        return;
      }
      const parsed = demoDataSummarySchema.safeParse(await response.json().catch(() => null));
      if (parsed.success) setSummary(parsed.data);
      else setError(t('error.unavailable'));
    })();
  };

  const remove = (): void => {
    setBusy(true);
    setError(null);
    void (async () => {
      const response = await session
        .request('/workspace/demo', { method: 'DELETE' })
        .catch(() => null);
      setBusy(false);
      if (response?.ok !== true) {
        setError(await errorFor(response));
        return;
      }
      const parsed = demoDataRemovedSchema.safeParse(await response.json().catch(() => null));
      setOpen(false);
      setToasts((current) => [
        ...current,
        {
          id: nextToastId(),
          title: t('demo.toast.removed'),
          ...(parsed.success && parsed.data.kept > 0
            ? { description: t('demo.toast.kept', { count: parsed.data.kept }) }
            : {}),
          variant: 'success',
        },
      ]);
    })();
  };

  return (
    <>
      <Banner
        action={
          canRemove ? (
            <Button variant="link" size="sm" className="h-auto px-0" onClick={openDialog}>
              {t('demo.banner.remove')}
            </Button>
          ) : undefined
        }
      >
        {t('demo.banner.text')}
      </Banner>

      <Dialog
        open={open}
        onOpenChange={setOpen}
        title={t('demo.remove.title')}
        description={t('demo.remove.lede')}
        closeLabel={t('common.close')}
        footer={
          <>
            {error === null ? null : (
              <p className="text-destructive mr-auto text-[12px]" role="alert">
                {error}
              </p>
            )}
            <Button
              variant="outline"
              onClick={() => {
                setOpen(false);
              }}
            >
              {t('common.cancel')}
            </Button>
            {/* ⚠️ The filled danger button, as the workspace deletion uses: this is final. */}
            <Button
              variant="destructive"
              disabled={summary === null || summary.removable === 0 || busy}
              onClick={remove}
            >
              {busy ? t('demo.remove.working') : t('demo.remove.confirm')}
            </Button>
          </>
        }
      >
        {summary === null ? (
          error === null ? (
            <p className="text-muted-foreground text-[12.5px]">{t('state.loading')}</p>
          ) : null
        ) : (
          <div className="flex flex-col gap-2 text-[13px]">
            <p>{t('demo.remove.removable', { count: summary.removable })}</p>
            {summary.kept > 0 ? (
              <p className="text-muted-foreground">
                {t('demo.remove.kept', { count: summary.kept })}
              </p>
            ) : null}
            <p className="font-medium">{t('demo.remove.final')}</p>
          </div>
        )}
      </Dialog>

      <Toaster
        toasts={[...toasts]}
        label={t('demo.toasts')}
        dismissLabel={t('common.close')}
        onDismiss={(id) => {
          setToasts((current) => current.filter((toast) => toast.id !== id));
        }}
      />
    </>
  );
}
